import React, { useEffect, useMemo, useState, useRef } from "react";
import { Box, Button, Tab, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import SurfaceCard from "../../../../components/Common/SurfaceCard";
import { TimePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { getWeekDays } from "../../../../utils/time";
import useAdministrationService from "../../../../services/administration/useAdministrationService";
import { DayKey, DayKeys, WorkingHoursMap, WorkingHoursValue } from "../../../../types/Administration";
import { useDebounce } from "../../../../hooks/useDebounce";
import dayjs from "dayjs";
import useToast, { ToastType } from "../../../../hooks/useToast";

enum ScheduleType {
    Open = "open",
    Close = "close",
}

const SCHEDULE_CLOSED = "closed";
const SCHEDULE_NON_STOP = "non-stop";

const DefaultWorkingHours: WorkingHoursMap = {
    monday: SCHEDULE_CLOSED,
    tuesday: SCHEDULE_CLOSED,
    wednesday: SCHEDULE_CLOSED,
    thursday: SCHEDULE_CLOSED,
    friday: SCHEDULE_CLOSED,
    saturday: SCHEDULE_CLOSED,
    sunday: SCHEDULE_CLOSED,
};

interface DaySchedulePanelProps {
    index: number;
    value: number;
    schedule: WorkingHoursValue;
    onTimePickerChange: (newValue: Dayjs, scheduleType: ScheduleType) => void;
    onCloseBtnClick?: () => void;
    onNonStopBtnClick?: () => void;
    onApplyAllBtnClick?: () => void;
}

function DaySchedulePanel({
    value,
    index,
    schedule,
    onTimePickerChange,
    onCloseBtnClick,
    onNonStopBtnClick,
    onApplyAllBtnClick,
}: DaySchedulePanelProps) {
    const { t } = useTranslation("common");

    const dayJsSchedule = useMemo(() => {
        if (schedule === SCHEDULE_CLOSED) {
            return {
                open: undefined,
                close: undefined,
            };
        }

        if (schedule === SCHEDULE_NON_STOP) {
            return {
                open: dayjs("00:00", "HH:mm"),
                close: dayjs("24:00", "HH:mm"),
            };
        }

        return {
            open: dayjs(schedule.open, "HH:mm"),
            close: dayjs(schedule.close, "HH:mm"),
        };
    }, [schedule]);

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            flex={1}
        >
            {value === index && (
                <Box p={3} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                    {/* nonstop and closed buttons */}
                    <Box gap={3} display="flex">
                        <Button
                            variant={schedule === SCHEDULE_NON_STOP ? "contained" : "outlined"}
                            color={schedule === SCHEDULE_NON_STOP ? "success" : "primary"}
                            onClick={onNonStopBtnClick}
                        >
                            {t("administration.locationAdministration.workingHours.nonStop")}
                        </Button>
                        <Button
                            variant={schedule === SCHEDULE_CLOSED ? "contained" : "outlined"}
                            color={schedule === SCHEDULE_CLOSED ? "success" : "primary"}
                            onClick={onCloseBtnClick}
                        >
                            {t("administration.locationAdministration.workingHours.closed")}
                        </Button>
                    </Box>
                    {/* time pickers */}
                    <Box display="flex" gap={2}>
                        <TimePicker
                            value={dayJsSchedule.open}
                            maxTime={dayJsSchedule.close && dayJsSchedule.close.format("HH:mm") !== "00:00" ? dayJsSchedule.close : undefined}
                            onAccept={(val) => onTimePickerChange(val!, ScheduleType.Open)}
                            label={t("administration.locationAdministration.workingHours.openLabel")}
                            sx={{ flex: 1 }}
                        />
                        <TimePicker
                            value={dayJsSchedule.close}
                            minTime={dayJsSchedule.open || undefined}
                            onAccept={(val) => onTimePickerChange(val!, ScheduleType.Close)}
                            label={t("administration.locationAdministration.workingHours.closeLabel")}
                            sx={{ flex: 1 }}
                        />
                    </Box>

                    <Box width="100%">
                        <Button
                            onClick={onApplyAllBtnClick}
                            fullWidth
                            variant="contained"
                            color="accent"
                        >
                            {t("administration.locationAdministration.workingHours.applyAll")}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function WorkingHours() {
    const [schedulePanelValue, setSchedulePanelValue] = useState(0);
    const [workingHours, setWorkingHours] = useState<WorkingHoursMap>(DefaultWorkingHours);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { getWorkingHours, updateWorkingHours } = useAdministrationService();
    const { t } = useTranslation("common");
    const { showToast } = useToast();
    const weekDays = getWeekDays(t);

    // Keep track of last saved state for rollback
    const lastSavedStateRef = useRef<WorkingHoursMap>(DefaultWorkingHours);

    const isValidSchedule = (schedule: WorkingHoursValue): boolean => {
        if (schedule === SCHEDULE_CLOSED || schedule === SCHEDULE_NON_STOP) {
            return true;
        }

        if (typeof schedule === 'object') {
            const hasOpen = schedule.open && schedule.open !== "00:00";
            const hasClose = schedule.close && schedule.close !== "00:00";

            // Both must be set or both must be unset
            return hasOpen === hasClose;
        }

        return false;
    };

    const isValidWorkingHours = (data: WorkingHoursMap): boolean => {
        return Object.values(data).every(schedule => isValidSchedule(schedule));
    };

    const performSave = async (dataToSave: WorkingHoursMap) => {
        if (!isValidWorkingHours(dataToSave)) {
            return;
        }

        try {
            await updateWorkingHours(dataToSave);
            lastSavedStateRef.current = dataToSave;
            showToast({
                message: t("administration.locationAdministration.workingHours.scheduleUpdatedSuccessfully"),
                type: ToastType.SUCCESS,
            });
        } catch (error) {
            setWorkingHours(lastSavedStateRef.current);
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
            });
        }
    };

    const debouncedSave = useDebounce(performSave, { delay: 1000 });

    useEffect(() => {
        const fetchWorkingHours = async () => {
            const data = await getWorkingHours();
            setWorkingHours(data);
            lastSavedStateRef.current = data;
        };

        try {
            fetchWorkingHours();
        } catch (error) {
            showToast({
                message: t("common.unknownError"),
                type: ToastType.ERROR,
            });
        }
    }, [getWorkingHours, showToast, t]);

    const onApplyAllBtnClick = (day: DayKey) => {
        const scheduleToApply = workingHours[day];
        const updatedWorkingHours = DayKeys.reduce<WorkingHoursMap>((acc, currentDay) => {
            acc[currentDay] = scheduleToApply;
            return acc;
        }, { ...workingHours });

        setWorkingHours(updatedWorkingHours);
        debouncedSave(updatedWorkingHours);
    };

    const handleTimePickerChange = (day: DayKey, newValue: Dayjs | typeof SCHEDULE_CLOSED | typeof SCHEDULE_NON_STOP, scheduleType?: ScheduleType) => {
        const updatedWorkingHours = (() => {
            const currentSchedule = workingHours[day];
            const isClosedOrNonStop = newValue === SCHEDULE_CLOSED || newValue === SCHEDULE_NON_STOP;
            if (isClosedOrNonStop) {
                return {
                    ...workingHours,
                    [day]: newValue,
                };
            }

            const newSchedule = {
                open: scheduleType === ScheduleType.Open
                    ? newValue.format("HH:mm")
                    : (typeof currentSchedule === 'object' && currentSchedule.open ? currentSchedule.open : "00:00"),
                close: scheduleType === ScheduleType.Close
                    ? newValue.format("HH:mm")
                    : (typeof currentSchedule === 'object' && currentSchedule.close ? currentSchedule.close : "00:00")
            };

            return {
                ...workingHours,
                [day]: newSchedule,
            };
        })();

        setWorkingHours(updatedWorkingHours);
        debouncedSave(updatedWorkingHours);
    }

    return (
        <>
            <SurfaceCard
                title={<Typography variant="h4">{t("administration.locationAdministration.workingHours.title")}</Typography>}
                contentSx={{ gap: 2 }}
            >
                <Box
                    display="flex"
                    flexGrow={1}
                    height={isMobile ? '100%' : 224}
                >
                    <Tabs
                        orientation="vertical"
                        variant="scrollable"
                        value={schedulePanelValue}
                        onChange={(_event, newValue) => setSchedulePanelValue(newValue)}
                        sx={{ borderRight: 1, borderColor: 'divider' }}
                    >
                        {weekDays.map((day, index) => (
                            <Tab
                                label={day}
                                key={day}
                                id={`vertical-tab-${index}`}
                                aria-controls={`vertical-tabpanel-${index}`}
                            />
                        ))}
                    </Tabs>
                    {DayKeys.map((day, index) => (
                        <DaySchedulePanel
                            value={schedulePanelValue}
                            index={index}
                            schedule={workingHours[day]}
                            key={day}
                            onTimePickerChange={(val, scheduleType) => handleTimePickerChange(day, val, scheduleType)}
                            onCloseBtnClick={() => handleTimePickerChange(day, SCHEDULE_CLOSED)}
                            onNonStopBtnClick={() => handleTimePickerChange(day, SCHEDULE_NON_STOP)}
                            onApplyAllBtnClick={() => onApplyAllBtnClick(day)}
                        />
                    ))}
                </Box>
            </SurfaceCard>
        </>
    );
}

export default WorkingHours;
